import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  add_reason,
  edit_reason_label,
  move_reason,
  remove_reason,
  validate_gen_globals
} from './gen_config';
import type { GenGlobals, GlobalsBounds, GlobalsKey } from './gen_config';
import { FIELD_CLASS, SectionCard, TOUCH_CLASS, btnStyle, fieldStyle } from './ui';

interface Props {
  globals: GenGlobals;
  bounds: GlobalsBounds;
  disabled: boolean;
  on_change: <K extends GlobalsKey>(key: K, value: GenGlobals[K]) => void;
}

const PRIORITY_KEYS = ['P0', 'P1', 'P2'] as const;

const ERROR_CLASS = 'm-0 text-xs text-state-error-text';
const FIELD_LABEL = 'w-[92px] shrink-0 text-content-muted';
const ROW = 'flex flex-wrap items-center gap-2 text-[12.5px] text-content-secondary';

// Square icon buttons for the skip-reason rows (↑ ↓ ×).
const ICON_BUTTON: CSSProperties = {
  ...btnStyle('neutral', 'row'),
  width: 28,
  padding: 0,
  fontSize: 13
};

function Group({
  title,
  children,
  className
}: {
  title: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-2 bg-surface-card px-3.5 py-3 ${className || ''}`}>
      <div className="text-[13px] font-semibold text-content-primary">{title}</div>
      {children}
    </div>
  );
}

// Team-wide sales task settings stored beside the rules, in three groups.
function GlobalSettingsCard({ globals: g, bounds, disabled, on_change }: Props) {
  const reasons = g.skip_reasons;
  const errors = validate_gen_globals(g);
  return (
    <SectionCard
      title="Global settings"
      testId="gen-global-settings"
      className={disabled ? 'opacity-70' : ''}
    >
      {/* gap-px over a line-coloured grid draws the dividers at any column count. */}
      <div className="grid gap-px bg-line-subtle md:grid-cols-2 xl:grid-cols-3">
        <Group title="Calling">
          <div className={ROW}>
            {/* Not a <label>: each time box carries its own "from" / "to" name. */}
            <span
              className={FIELD_LABEL}
              title="The student's local calling hours. Tasks are still created at any hour; outside these hours a task is grayed out and calling or messaging asks to confirm."
            >
              Call window
            </span>
            <span className="inline-flex items-center gap-2">
              <input
                id="gen-call-start"
                type="time"
                aria-label="Call window from"
                value={g.call_window_start}
                disabled={disabled}
                onChange={(e) => on_change('call_window_start', e.target.value)}
                className={FIELD_CLASS}
                style={fieldStyle(112)}
              />
              to
              <input
                id="gen-call-end"
                type="time"
                aria-label="Call window to"
                value={g.call_window_end}
                disabled={disabled}
                onChange={(e) => on_change('call_window_end', e.target.value)}
                className={FIELD_CLASS}
                style={fieldStyle(112)}
              />
            </span>
          </div>
          {errors.call_window ? (
            <p role="alert" className={ERROR_CLASS}>
              {errors.call_window}
            </p>
          ) : null}
          <div className={ROW}>
            <label htmlFor="gen-claim-idle" className={FIELD_LABEL}>
              Claim idle
            </label>
            <input
              id="gen-claim-idle"
              type="number"
              aria-label="Claim idle (minutes)"
              min={bounds.min_claim_idle_minutes}
              max={bounds.max_claim_idle_minutes}
              value={g.claim_idle_minutes ?? ''}
              disabled={disabled}
              onChange={(e) => on_change('claim_idle_minutes', e.target.value)}
              className={FIELD_CLASS}
              style={fieldStyle(64)}
            />
            min
          </div>
          <label
            htmlFor="gen-require-outcome"
            className="flex items-center gap-2 text-[12.5px] text-content-primary max-sm:min-h-[40px]"
          >
            <input
              id="gen-require-outcome"
              type="checkbox"
              checked={g.require_outcome}
              disabled={disabled}
              onChange={(e) => on_change('require_outcome', e.target.checked)}
              style={{ margin: 0 }}
            />
            Require an outcome to close a task
          </label>
          <div className="flex items-start gap-2 text-[12.5px] text-content-primary">
            <input
              id="gen-duplicate-account-skip"
              type="checkbox"
              checked={g.duplicate_account_skip}
              disabled={disabled}
              aria-describedby="gen-duplicate-account-skip-help"
              onChange={(e) => on_change('duplicate_account_skip', e.target.checked)}
              style={{ margin: '3px 0 0' }}
            />
            <span className="min-w-0">
              <label htmlFor="gen-duplicate-account-skip">Skip duplicate accounts</label>
              <span
                id="gen-duplicate-account-skip-help"
                className="block text-[11.5px] text-content-muted"
              >
                Skip a task when an older account with the same phone number is already a member or
                has attended a session
              </span>
            </span>
          </div>
        </Group>

        <Group title="Unclaimed alerts">
          <div className={ROW}>
            <span className="text-content-muted">After</span>
            {PRIORITY_KEYS.map((p) => (
              <label
                key={p}
                htmlFor={`gen-buffer-${p}`}
                className="inline-flex items-center gap-1 text-content-secondary"
              >
                {p}
                <input
                  id={`gen-buffer-${p}`}
                  type="number"
                  aria-label={`Unclaimed alert buffer ${p} (minutes)`}
                  min={bounds.min_unclaimed_buffer_minutes}
                  max={bounds.max_unclaimed_buffer_minutes}
                  value={g.unclaimed_alert_buffer_minutes[p] ?? ''}
                  disabled={disabled}
                  onChange={(e) =>
                    on_change('unclaimed_alert_buffer_minutes', {
                      ...g.unclaimed_alert_buffer_minutes,
                      [p]: e.target.value
                    })
                  }
                  className={FIELD_CLASS}
                  style={fieldStyle(60)}
                />
              </label>
            ))}
            <span className="text-content-muted">min</span>
          </div>
          <label htmlFor="gen-alert-mentions" className="flex flex-col gap-1 text-xs">
            <span className="text-content-muted">
              Slack mentions <span className="text-[11.5px]">(user IDs, comma separated)</span>
            </span>
            <input
              id="gen-alert-mentions"
              type="text"
              aria-label="Unclaimed alert mentions (Slack user IDs, comma separated)"
              value={g.unclaimed_alert_mentions}
              disabled={disabled}
              onChange={(e) => on_change('unclaimed_alert_mentions', e.target.value)}
              aria-invalid={errors.bad_mentions.length > 0 || undefined}
              className={FIELD_CLASS}
              style={fieldStyle('100%', errors.bad_mentions.length > 0)}
            />
          </label>
          {errors.bad_mentions.length ? (
            <span role="alert" className={ERROR_CLASS}>
              Not a Slack user ID (e.g. U012AB3CD): {errors.bad_mentions.join(', ')}
            </span>
          ) : null}
          <label htmlFor="gen-alert-channel" className="flex flex-col gap-1 text-xs">
            <span className="text-content-muted">
              Slack channel <span className="text-[11.5px]">(channel ID; empty = alerts off)</span>
            </span>
            <input
              id="gen-alert-channel"
              type="text"
              aria-label="Unclaimed alert channel (Slack channel ID; empty = alerts off)"
              value={g.unclaimed_alert_channel_id}
              disabled={disabled}
              maxLength={32}
              placeholder="C012AB3CD"
              onChange={(e) => on_change('unclaimed_alert_channel_id', e.target.value)}
              aria-invalid={!!errors.alert_channel || undefined}
              className={FIELD_CLASS}
              style={fieldStyle('100%', !!errors.alert_channel)}
            />
          </label>
          {errors.alert_channel ? (
            <span role="alert" className={ERROR_CLASS}>
              {errors.alert_channel}
            </span>
          ) : null}
        </Group>

        <Group title="Skip reasons" className="md:col-span-2 xl:col-span-1">
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {reasons.map((r, i) => {
              const empty = errors.empty_reasons.includes(i);
              const duplicate = errors.duplicate_reasons.includes(i);
              return (
                <li key={r.id ?? r.code} className="flex flex-col gap-0.5">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <input
                      type="text"
                      aria-label={`Skip reason ${i + 1}`}
                      value={r.label}
                      disabled={disabled}
                      maxLength={64}
                      aria-invalid={duplicate || empty || undefined}
                      onChange={(e) =>
                        on_change('skip_reasons', edit_reason_label(reasons, i, e.target.value))
                      }
                      className={`min-w-0 flex-1 ${FIELD_CLASS}`}
                      style={fieldStyle(undefined, duplicate || empty)}
                    />
                    <code
                      className="max-w-[40%] truncate text-[11px] text-content-muted"
                      title={r.code || undefined}
                    >
                      {r.code || '—'}
                    </code>
                    <button
                      type="button"
                      aria-label={`Move ${r.label || 'reason'} up`}
                      disabled={disabled || i === 0}
                      onClick={() => on_change('skip_reasons', move_reason(reasons, i, -1))}
                      className={TOUCH_CLASS}
                      style={ICON_BUTTON}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${r.label || 'reason'} down`}
                      disabled={disabled || i === reasons.length - 1}
                      onClick={() => on_change('skip_reasons', move_reason(reasons, i, 1))}
                      className={TOUCH_CLASS}
                      style={ICON_BUTTON}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${r.label || 'reason'}`}
                      disabled={disabled || reasons.length <= 1}
                      title={reasons.length <= 1 ? 'Keep at least one reason' : undefined}
                      onClick={() => on_change('skip_reasons', remove_reason(reasons, i))}
                      className={TOUCH_CLASS}
                      style={ICON_BUTTON}
                    >
                      ×
                    </button>
                  </div>
                  {empty ? (
                    <span className={ERROR_CLASS}>Enter a label, or remove this reason</span>
                  ) : null}
                  {duplicate ? (
                    <span className={ERROR_CLASS}>Same code as another reason</span>
                  ) : null}
                </li>
              );
            })}
          </ol>
          <div>
            <button
              type="button"
              disabled={disabled}
              onClick={() => on_change('skip_reasons', add_reason(reasons))}
              className={TOUCH_CLASS}
              style={{
                ...btnStyle('ghost', 'row'),
                padding: 0,
                ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : null)
              }}
            >
              <span aria-hidden="true">+</span> Add reason
            </button>
          </div>
        </Group>
      </div>
    </SectionCard>
  );
}

export default GlobalSettingsCard;
